/** Talking to the ImageSync API: JSON in and out, the session's CSRF token on every change, and plain-English errors. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public field?: string) { super(message); this.name = 'ApiError'; }
}

let csrf: string | null = null;
let signedOut: (() => void) | null = null;
export const setCsrf = (t: string | null): void => { csrf = t; };
/** Called when a request finds the session has ended (idle timeout, signed out elsewhere, account turned off). */
export const onSignedOut = (fn: () => void): void => { signedOut = fn; };

const FALLBACK: Record<number, string> = {
  400: 'That request wasn’t right. Check the form and try again.', 401: 'Your session has ended. Sign in again.',
  403: 'This account can’t do that.', 404: 'That isn’t here any more.', 409: 'That conflicts with a change someone else made. Reload and try again.',
  413: 'That file is too large.', 429: 'Too many requests. Wait a moment and try again.', 503: 'The server is busy. Try again in a moment.',
};
const messageFor = (status: number) => FALLBACK[status] ?? (status >= 500 ? 'Something went wrong on the server. Try again, and tell the office if it keeps happening.' : 'The request failed.');

async function failure(r: Response, path: string): Promise<ApiError> {
  let data: { error?: string; message?: string; field?: string } = {};
  if ((r.headers.get('content-type') ?? '').includes('json')) data = await r.json().catch(() => ({}));
  if (r.status === 401 && signedOut && !path.startsWith('/api/v1/auth/login') && !path.startsWith('/api/v1/auth/me')) signedOut();
  return new ApiError(r.status, data.error ?? 'error', data.message ?? messageFor(r.status), data.field);
}

async function send(method: string, path: string, body?: unknown, accept = 'application/json'): Promise<Response> {
  const headers: Record<string, string> = { accept };
  if (body !== undefined && !(body instanceof FormData)) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrf) headers['x-csrf-token'] = csrf;
  try {
    return await fetch(path, { method, headers, credentials: 'same-origin', ...(body !== undefined ? { body: body instanceof FormData ? body : JSON.stringify(body) } : {}) });
  } catch {
    throw new ApiError(0, 'network', 'The server couldn’t be reached. Check the connection and try again.');
  }
}

export async function api<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const r = await send(method, path, body);
  if (!r.ok) throw await failure(r, path);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}
export const get = <T>(path: string): Promise<T> => api<T>('GET', path);
export const post = <T>(path: string, body: unknown = {}): Promise<T> => api<T>('POST', path, body);
export const put = <T>(path: string, body: unknown): Promise<T> => api<T>('PUT', path, body);
export const patch = <T>(path: string, body: unknown): Promise<T> => api<T>('PATCH', path, body);
export const del = <T>(path: string): Promise<T> => api<T>('DELETE', path);

/** A file from the API (a PDF copy, a CSV export), with the name the server gave it. */
export async function fetchFile(method: 'GET' | 'POST', path: string, body?: unknown): Promise<{ blob: Blob; filename: string }> {
  const r = await send(method, path, body, '*/*');
  if (!r.ok) throw await failure(r, path);
  const cd = r.headers.get('content-disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  return { blob: await r.blob(), filename: m?.[1] ?? 'download' };
}

/** A multipart upload with progress (fetch can't report upload progress). */
export function upload<T>(path: string, form: FormData, onProgress: (fraction: number) => void): { done: Promise<T>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const done = new Promise<T>((resolve, reject) => {
    xhr.open('POST', path);
    xhr.withCredentials = true;
    if (csrf) xhr.setRequestHeader('x-csrf-token', csrf);
    xhr.setRequestHeader('accept', 'application/json');
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let data: { error?: string; message?: string; field?: string } = {};
      try { data = JSON.parse(xhr.responseText || '{}'); } catch { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else {
        if (xhr.status === 401 && signedOut) signedOut();
        reject(new ApiError(xhr.status, data.error ?? 'error', data.message ?? messageFor(xhr.status), data.field));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'The upload stopped. Check the connection and try again.'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Upload cancelled.'));
    xhr.send(form);
  });
  return { done, abort: () => xhr.abort() };
}
